'use client'

import { cn } from '@repo/ui/lib/utils'
import { ComponentPropsWithoutRef, FunctionComponent, useEffect, useRef } from 'react'

interface Props extends ComponentPropsWithoutRef<'video'> {
  className?: string
  threshold?: IntersectionObserverInit['threshold']
}

const Video: FunctionComponent<Props> = ({ src, className, threshold = 0.1, ...rest }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Tracks if the video is currently visible in the viewport.
  const isIntersectingRef = useRef(false)

  // Tracks if the video has buffered enough to start playing.
  const isReadyRef = useRef(false)

  useEffect(() => {
    const videoElement = videoRef.current

    if (!videoElement) {
      return
    }

    // Immediately mark as ready if already loaded enough.
    if (videoElement.readyState >= 3) {
      isReadyRef.current = true
    }

    // Plays the video only when it's both visible and ready.
    const tryPlay = () => {
      if (isIntersectingRef.current && isReadyRef.current) {
        videoElement.play().catch((error) => {
          console.error('Error attempting to play.', { error, src })
        })
      }
    }

    // Mark the video as ready and try to play it (if visible).
    const handleCanPlay = () => {
      isReadyRef.current = true
      tryPlay()
    }

    // Listen for when the video is ready to play.
    videoElement.addEventListener('canplay', handleCanPlay)

    // Observe the video to detect when it enters or exits the viewport, so we can start or pause playback accordingly.
    const handleIntersection: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        isIntersectingRef.current = entry.isIntersecting

        if (entry.isIntersecting) {
          tryPlay()
        } else {
          videoElement.pause()
        }
      })
    }

    // Observe when the video enters or exits the viewport.
    observerRef.current = new IntersectionObserver(handleIntersection, { threshold })
    observerRef.current.observe(videoElement)

    return () => {
      if (observerRef.current && videoElement) {
        observerRef.current.unobserve(videoElement)
      }

      videoElement.removeEventListener('canplay', handleCanPlay)
    }
  }, [src, threshold])

  return <video ref={videoRef} className={cn('size-full object-cover', className)} src={src} muted={true} loop={true} playsInline={true} {...rest} />
}

export default Video
